/**
 * Component: Product form
 * ------------------------------------------------------------------------------
 * Highly coupled scripts to control the behaviour of a product form.
 *
 * @namespace productForm
 */
import {formatMoney} from '@shopify/theme-currency';

import cssClasses from '~/helpers/cssClasses';
import breakpoints from '~/helpers/breakpoints';
import {on, decodeHTMLEntities, tagByName} from '~/helpers/utils';
import bind from '~/mixins/bind';
import productEngraving from '~/components/product-engraving';
import ProductSwatches from '~/components/product-swatches';

/**
 * DOM selectors.
 */
const selectors = {
  containerEngraving: '[js-engraving="container"]',
  addToCart: '[js-product-form="addToCart"]',
  comparePrice: '[js-product-form="comparePrice"]',
  container: '[js-product-form="container"]',
  masterSelector: '[js-product-form="masterSelector"]',
  maxQuantityInput: '[js-product-form="maxQuantityInput"]',
  notification: '[js-product-form="notification"]',
  optionSelector: '[js-product-form="optionSelector"]',
  price: '[js-product-form="price"]',
  priceWrapper: '[js-product-form="priceWrapper"]',
  productJson: '[js-product-json]',
  share: '[js-product-form="share"]',
  quantitySelector: '[js-quantity-selector="container"]',
  size: '[js-product-swatches="size"]',
  checkboxEngraving: '[js-engraving="checkbox"]',
  productEngraving: '[js-engraving="product-engraving"]',
  inputEngraving: '[js-engraving="input"]',
  btnEngraving: '[js-engraving="button"]',
};

const formUpdateEvent = new CustomEvent('Alessi:formStateUpdate');

/**
 * Create a new product form object.
 * @param {Object} config - Configuration.
 */
export default (config) => {

  /**
   * Instance settings.
   */
  const settings = {
    enableHistoryState: config.enableHistoryState || false,
  };

  /**
   * DOM node selectors.
   */
  const container = document.querySelector(`[js-product-form="${config.container}"]`);
  const nodeSelectors = {
    containerEngraving: container.querySelector(selectors.containerEngraving),
    addToCart: container.querySelector(selectors.addToCart),
    comparePrice: container.querySelector(selectors.comparePrice),
    masterSelector: container.querySelector(selectors.masterSelector),
    optionSelector: container.querySelectorAll(selectors.optionSelector),
    price: container.querySelector(selectors.price),
    priceWrapper: container.querySelector(selectors.priceWrapper),
    quantitySelector: container.querySelector(selectors.quantitySelector),
    maxQuantityInput: container.querySelector(selectors.maxQuantityInput),
    productJson: container.querySelector(selectors.productJson),
    share: container.querySelector(selectors.share),
    size: container.querySelector(selectors.size),
    checkboxEngraving: document.querySelector(selectors.checkboxEngraving),
    productEngraving: document.querySelector(selectors.productEngraving),
    inputEngraving: document.querySelector(selectors.inputEngraving),
    btnEngraving: document.querySelector(selectors.btnEngraving),
  };

  /**
   * Initialise component bind.
   */
  const binder = bind(container);

  /**
   * Global instances.
   */
  const productData = Object.freeze(JSON.parse(nodeSelectors.productJson.innerHTML));
  const variantData = productData.variants;


  let currentVariant = {};

  /**
   * Initialise component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    if (currentVariant) {
      currentVariant = getVariantFromOptions();
    }


    setDefaultState();
    setEventListeners();
    setEventBusListeners();
    binder.set();
  }

  /**
   * Set default state.
   */
  function setDefaultState() {
    ProductSwatches({
      data: productData,
    }).init();
    if (theme.engraving.allow === true) {
      productEngraving().init();
    }
    updateFormState();

  }

  /**
   * Update notification state.
   * @param {Object} variant - Current variant object.
   */
  function updateNotificationState(variant) {
    if (isNotificationCreated()) {
      container.querySelector(selectors.notification).remove();
    }

    if (variant) {
      const quantity = getVariantInventoryQuantity(variant);

      if (!quantity) {
        return;
      }

      if (quantity <= theme.features.inventoryThreshold && quantity > 0) {
        container.appendChild(createNotification(quantity));
      }
    }
  }

  /**
   * Check if notification exists.
   * @returns {Boolean}
   */
  function isNotificationCreated() {
    return Boolean(container.querySelector(selectors.notification));
  }

  /**
   * Create notification node.
   * @param {Number} quantity - Current variant inventory quantity.
   * @returns {HTMLElement}
   */
  function createNotification(quantity) {
    const message = getLowStockMessage(quantity);
    const notification = document.createElement('div');

    notification.classList.add('product-form__notification');
    notification.innerHTML = getNotificationTemplate(message);
    notification.setAttribute('js-product-form', 'notification');

    return notification;
  }

  /**
   * Get notification template.
   * @param {String} message - text to render inside notification.
   * @returns {String}
   */
  function getNotificationTemplate(message) {
    return `
      <p class="label text-center color--brand">
        ${message}
      </p>
    `;
  }

  /**
   * Get low stock notification message correctly pluralised.
   * @param {Number} quantity - Quantity of line item.
   * @returns {String}
   */
  function getLowStockMessage(quantity) {
    if (quantity === 1) {
      return theme.low_stock_message.single;
    }

    return theme.low_stock_message.multiple.replace('%quantity%', quantity);
  }

  /**
   * Get variant inventory quantity from product-json.liquid variant data.
   * @param {Object || undefined} - Variant object.
   */
  function getVariantInventoryQuantity(variant) {
    if (!variantData.length) { return 0; }
    if (checkSizeValue() === 0) { return 0; }

    return Object.keys(variantData).map((key) => {
      if (variantData[key].id === variant.id) {
        return variantData[key].inventory_quantity;
      }

      return null;
    }).filter(Boolean)[0];
  }

  /**
   * Check size value;
  */
  function checkSizeValue() {
    if (nodeSelectors.size) {

      if (nodeSelectors.size.value === '') {
        return 0;
      }
    }

    return false;
  }

  /**
   * Set EventBus listeners.
   */
  function setEventBusListeners() {
    Frame.EventBus.listen('Variant:priceChange', (response) => onPriceChange(response));
    Frame.EventBus.listen('ProductSwatches:available', (response) => handleSwatchAvailability(response));
    Frame.EventBus.listen('ProductSwatches:optionSelected', (response) => handleOptionChange(response));
    Frame.EventBus.listen('ProductSwatches:reset', () => resetForm());
    Frame.EventBus.listen('ProductSwatches:soldOut', () => updateAddToCartState(decodeHTMLEntities(theme.strings.soldOut)));
    Frame.EventBus.listen('AjaxCart:itemAdded', () => updateDataLayer());
  }

  /**
   * Set event listeners on select elements.
   */
  function setEventListeners() {
    [...nodeSelectors.optionSelector].forEach((element) => {
      if (element.nodeName === 'SELECT') {
        on('change', element, () => handleSelectChange());
      }
    });
    if (theme.engraving.allow === true) {
      on('mouseover', nodeSelectors.addToCart, () => submitLineProperties());
      on('keydown', nodeSelectors.inputEngraving, () => submitLineProperties());
      on('blur', nodeSelectors.inputEngraving, (e) => {
        e.target.value = e.target.value.substring(0, e.target.maxLength);
      });
    }
    window.addEventListener('load', updateStickyPosition);
  }

  /**
   * Get currently selected options from product form.
   * - Works with all form elements with an option selector selector.
   * @returns {Array}
   */
  function getCurrentOptions() {
    const currentOptions = [];

    [...nodeSelectors.optionSelector].forEach((element) => {
      const type = element.getAttribute('type');
      const currentOption = {};

      if (type === 'radio' || type === 'checkbox') {
        if (element.checked) {
          currentOption.value = element.value;
          currentOption.index = element.dataset.index;

          currentOptions.push(currentOption);
        }
      } else {
        currentOption.value = element.value;
        currentOption.index = element.dataset.index;

        currentOptions.push(currentOption);
      }
    });

    return currentOptions;
  }

   /**
    * Find variant from product.variants based on selected values.
    * @returns {Object|undefined}
    */
  function getVariantFromOptions() {
    const selectedValues = getCurrentOptions();
    const variants = productData.variants;
    let found = false;

    variants.forEach((variant) => {
      let satisfied = true;

      selectedValues.forEach((option) => {
        if (satisfied) {
          satisfied = (option.value === variant[option.index]);
        }
      });

      if (satisfied) {
        found = variant;
      }
    });

    return found || '';
  }

  /**
   * Event handler for when a variant price changes.
   * @param {Object} response - Selected variant data.
   */
  function onPriceChange(response) {
    const form = document.querySelector(`[js-product-form="${config.container}"]`);
    const priceSelector = form.querySelector(selectors.price);

    priceSelector.setAttribute('data-price', response.price);
    priceSelector.innerText = formatMoney(response.price, theme.moneyFormat);

    if (form.querySelector(selectors.comparePrice)) {
      const comparePriceSelector = form.querySelector(selectors.comparePrice);
      comparePriceSelector.setAttribute('data-price', response.compare_at_price);

      if (response.compare_at_price > response.price) {
        comparePriceSelector.innerText = formatMoney(response.compare_at_price, theme.moneyFormat);
        comparePriceSelector.classList.remove(cssClasses.hidden);

      } else {
        comparePriceSelector.innerText = '';
        comparePriceSelector.classList.add(cssClasses.hidden);
      }
    }

    if (theme.features.currencyConverter) {
      Frame.EventBus.emit('Currency:convertAll');
    }
  }

  /**
   * Handle default availability.
   */
  function handleSwatchAvailability(response) {
    if(nodeSelectors.addToCart) {
      nodeSelectors.addToCart.dataset.status = 'ready';
      nodeSelectors.addToCart.removeAttribute('disabled');

    }

    if (!response.formCompleted) {
      disableAddToCart(decodeHTMLEntities(theme.strings.prompt));
    }
  }

  /**
   * Event handler for when an input changes on the form.
   * @param {Object} response - Pay load from option selected.
   */
  function handleOptionChange(response) {
    Frame.EventBus.emit('ProductForm:change', {
      currentOptions: getCurrentOptions(),
      currentVariant: (response.formCompleted ? getVariantFromOptions() : null),
      currentInventory: getVariantInventoryQuantity(getVariantFromOptions()),

    });


    if (!response.formCompleted) { return; }

    updateFormState();
  }

  /**
   * Event handler for when a select element changes on the store.
   */
  function handleSelectChange() {
    updateFormState();
  }

  /**
   * Update form state based on current selections.
   */
  function updateFormState() {
    const variant = getVariantFromOptions();

    updateNotificationState(variant);
    updateAddToCartState(variant);

    if (!variant) {
      return;
    }

    updateMaxQuantityState(variant);
    updateMasterSelect(variant);
    updatePrice(variant);

    currentVariant = variant;
    if (theme.engraving.allow === true){
      submitLineProperties()
    }


    if (settings.enableHistoryState) {
      updateHistoryState(variant);
    }

    document.dispatchEvent(formUpdateEvent);
  }



function submitLineProperties() {
  var randomNumber = Math.floor(Math.random() * (99999999 - 10000000 + 1)) + 10000000;

  let engravingValue = nodeSelectors.inputEngraving.value;
  if(nodeSelectors.checkboxEngraving.checked && engravingValue !== '' && currentVariant.available) {
    nodeSelectors.productEngraving.innerHTML = `
      <input type="hidden" name="items[0][quantity]" value="1">
      <input type="hidden" name="items[0][id]" value="${theme.engraving.product}">
      <input type="hidden" name="items[0]properties[_engraving-service]" value="Yes"/>
      <input type="hidden" name="items[0]properties[Info]" value="${theme.engraving.line_properties_item}"/>

      <input type="hidden" name="properties[ENGRAVING]" value="${engravingValue}"/>
      <input type="hidden" name="properties[_TMS]" value="${randomNumber}"/>
    `
  }
}



  /**
   * Updates the DOM state of the add to cart button.
   * @param {{Object || undefined}} variant - Currently selected variant.
   */
  function updateAddToCartState(variant) {
    if (variant) {
      enableAddToCart();
      addEngraving();
      nodeSelectors.priceWrapper.classList.remove(cssClasses.hidden);

    } else {
      disableAddToCart();
      removeEngraving();
      nodeSelectors.priceWrapper.classList.add(cssClasses.hidden);
      if(nodeSelectors.addToCart) {
        nodeSelectors.addToCart.innerText = decodeHTMLEntities(theme.strings.unavailable);
      }
      return;
    }

    if (variant.available) {
      enableAddToCart();
      addEngraving();
      if(nodeSelectors.addToCart) nodeSelectors.addToCart.innerText = decodeHTMLEntities(theme.strings.addToCart);

    } else {
      disableAddToCart();
      removeEngraving();
      if(nodeSelectors.addToCart) nodeSelectors.addToCart.innerText = decodeHTMLEntities(theme.strings.soldOut);
    }
  }

  /**
   * Update max quantity value of quantity selectors.
   * @param {Object} variant - Currently selected variant.
   */
  function updateMaxQuantityState(variant) {
    const quantity = (
      getVariantInventoryQuantity(variant) > 0
      ? getVariantInventoryQuantity(variant)
      : 999
    );

    nodeSelectors.maxQuantityInput.value = quantity;
  }

  /**
   * Trigger event when variant price changes.
   * @param {Object} variant - Currently selected variant.
   */
  function updatePrice(variant) {
    Frame.EventBus.emit('Variant:priceChange', variant);
  }

  /**
   * Update hidden master select of variant change.
   * @param {Object} variant - Currently selected variant.
   */
  function updateMasterSelect(variant) {
    if(variant.id) {
      nodeSelectors.masterSelector.value = variant.id;
    }


  }

  /**
   * Update history state for product deep linking.
   * @param {Object} variant - Currently selected variant.
   */
  function updateHistoryState(variant) {
    if (!window.history.replaceState || !variant) {
      return;
    }

    const newurl = `
      ${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}
    `;
    window.history.replaceState({path: newurl}, '', newurl);
  }

  /**
   * Reset form and assign prompt text to add to cart button.
   */
  function resetForm() {
    disableAddToCart();

    if (productData.available && nodeSelectors.addToCart) {
      nodeSelectors.addToCart.innerText = decodeHTMLEntities(theme.strings.prompt);
    }
  }

  /**
   * Add disable state to add to cart button.
   * @param {String} label - Text to use on button.
   */
  function disableAddToCart(label = decodeHTMLEntities(theme.strings.unavailable)) {
    if (nodeSelectors.addToCart && !isAddToCartFirstLoad()) {
      nodeSelectors.addToCart.setAttribute('disabled', true);
      nodeSelectors.addToCart.classList.add('unavailable');
    }

    if(nodeSelectors.addToCart) nodeSelectors.addToCart.innerText = label;
  }

  /**
   * Set add to cart button to active.
   */
  function enableAddToCart() {
    if(!nodeSelectors.addToCart) {
      console.warn('Product Form not initialized because it\'s missing add to cart button');
      return;
    } else {
      nodeSelectors.addToCart.removeAttribute('disabled');
      nodeSelectors.addToCart.classList.remove('unavailable');
      nodeSelectors.addToCart.innerText = decodeHTMLEntities(theme.strings.addToCart);
    }
  }

  /**
   * Check if it's the first load of the CTA.
   */
  function isAddToCartFirstLoad() {
    if(nodeSelectors.addToCart) {
      return nodeSelectors.addToCart.dataset.status === 'chooseOptions';
    }
  }

  function removeEngraving() {
    if (theme.engraving.allow === true){
      nodeSelectors.containerEngraving.classList.add(cssClasses.hidden)
    }
  }
  function addEngraving() {
    if (theme.engraving.allow === true){
      nodeSelectors.containerEngraving.classList.remove(cssClasses.hidden)
    }
  }

    /**
   * Check if browser window is desktop screen size.
   * @returns {Boolean}
   */
    function isMobile() {
      return window.matchMedia(`(max-width: ${breakpoints.large})`).matches;
    }

  function updateStickyPosition() {
    const stickyElement = document.querySelector('.product-form__add-to-cart');
    const footerGroupElement = document.querySelector('.shopify-section-group-footer-group');


    if (!stickyElement || !footerGroupElement) {
      console.error('Elementi non trovati.');
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    };

    function handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            stickyElement.classList.remove('product-form__add-to-cart--sticky');
          }, 200);
        } else {
          setTimeout(() => {
            stickyElement.classList.add('product-form__add-to-cart--sticky');
          }, 200);
        }
      });
    }
    if(isMobile()){
      const observer = new IntersectionObserver(handleIntersection, options);
      observer.observe(footerGroupElement);
    }
  }

  function updateDataLayer() {
    window.dataLayer.push({
      event: 'GAevent',
      eventID: '14',
      eventCategory: 'product',
      eventAction: 'button',
      eventLabel: 'add_to_cart',
    });

    window.dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: window.theme.currency,
        eventPageLocator: 'product_page',
        add: {
          actionField: {
            list: localStorage.getItem('datalayerList'),
          },
          products: [{
            name: productData.title,
            id: currentVariant.sku,
            price: (currentVariant.price / 100),
            brand: 'alessi',
            category: tagByName("category", productData.tags),
            variant: currentVariant.option1,
            quantity: 1,
            dimension15: tagByName("macro_category", productData.tags),
            dimension16: currentVariant.option2,
          }],
        },
      },
    });
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    getVariantFromOptions,
  });
};
